import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import mockData from '@/data/mock-data.json';

type Role = 'Admin' | 'Account Manager' | 'Client';

interface UserInfo {
  id: string;
  username: string;
  firstName: string;
  lastName: string;
  email: string;
  fullName: string;
}

interface RoleContextType {
  role: Role;
  roles: string[];
  user: UserInfo | undefined;
  isAdmin: boolean;
  setRole: (role: Role) => void;
}

const defaultRole = (mockData.currentUser.roles[0] as Role) ?? 'Account Manager';

const RoleContext = createContext<RoleContextType>({
  role: defaultRole,
  roles: mockData.currentUser.roles,
  user: undefined,
  isAdmin: mockData.currentUser.roles.includes('Admin'),
  setRole: () => {},
});

export const RoleProvider = ({ children }: { children: ReactNode }) => {
  const [role, setRole] = useState<Role>(defaultRole);
  const [roles] = useState<string[]>(mockData.currentUser.roles);
  const [user, setUser] = useState<UserInfo | undefined>();

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const { getUserProfile } = await import('../auth/keycloak');
        const profile = getUserProfile();
        if (profile.id) {
          setUser(profile);
        }
      } catch {
        // Keycloak not initialized (dev mode) — keep mock defaults
      }
    };
    loadProfile();
  }, []);

  const isAdmin = roles.includes('Admin') || roles.includes('admin');

  return (
    <RoleContext.Provider value={{ role, roles, user, isAdmin, setRole }}>
      {children}
    </RoleContext.Provider>
  );
};

export const useRole = () => useContext(RoleContext);
